import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Grid from '@mui/material/Grid'
import KPI from '../../components/KPI'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import useKpis from '../../hooks/kpis/useKpis'
import useMe from '../../hooks/users/useMe'
import useOrganizations from '../../hooks/kpis/useOrganizations'
import { addAllOption, a11yProps, isValidDate } from '../../utils/helpers'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { IconButton, InputAdornment, Popover } from '@mui/material'
import DateRangeIcon from '@mui/icons-material/DateRange'
import { DateRange } from 'react-date-range'
import { format, isBefore, isValid, isSameDay } from 'date-fns'
import { kpiTabs } from '../../utils/kpiTabs'
import { useStore } from '../../hooks/globals/useStore'
import shallow from 'zustand/shallow'
import Alert from '@mui/material/Alert'
import TabPanel from '../../components/TabPanel'

const ALL_ORGANIZATION_OPTION = {
  Id: -1,
  Name: 'All',
  OrganizationCode: 'All'
}

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

const Home = () => {
  const { kpiTimeSpan, kpiStartDate, kpiEndDate, kpiDateRange, setKpiTimeSpan, setKpiDates, kpiPreviousStartDate, kpiPreviousEndDate, timezone } = useStore((state) => ({
    kpiTimeSpan: state.kpiTimeSpan,
    kpiStartDate: state.kpiStartDate,
    kpiEndDate: state.kpiEndDate,
    kpiDateRange: state.kpiDateRange,
    setKpiTimeSpan: state.setKpiTimeSpan,
    setKpiDates: state.setKpiDates,
    kpiPreviousStartDate: state.kpiPreviousStartDate,
    kpiPreviousEndDate: state.kpiPreviousEndDate,
    timezone: state.timezone
  }), shallow)

  const [selectedOrganizations, setSelectedOrganizations] = useState([ALL_ORGANIZATION_OPTION])
  const [anchorEl, setAnchorEl] = useState(null)
  const [displayCalendar, setDisplayCalendar] = useState(false)
  const [dateRangeError, setDateRangeError] = useState(false)

  const { data: kpis, isLoading: loadingKpis, isSuccess: loadedKpis, refetch: fetchKpis, isRefetching, isStale } = useKpis(selectedOrganizations, kpiStartDate, kpiEndDate, kpiPreviousStartDate, kpiPreviousEndDate, timezone)
  const { isSuccess: gotMe } = useMe()
  const { data: organizations, refetch: fetchOrganizations, isStale: organizationsAreStale, isSuccess } = useOrganizations()

  const handleChange = (event, newValue) => {
    setKpiTimeSpan(newValue)
  }

  useEffect(() => {
    if (kpiTimeSpan >= 0 && isStale && gotMe && selectedOrganizations) {
      fetchKpis()
    }
  }, [kpiTimeSpan, isStale, gotMe, selectedOrganizations])

  useEffect(() => {
    if (organizationsAreStale) {
      fetchOrganizations()
    }
  }, [organizationsAreStale])

  const handleChangeOrganizations = (event, newValue) => {
    if (newValue.length === 0) {
      setSelectedOrganizations([ALL_ORGANIZATION_OPTION])
    } else {
      const allIsFirst = newValue[0].OrganizationCode === 'All'
      if (allIsFirst) {
        const newValues = newValue.filter((v) => v.OrganizationCode !== 'All')
        setSelectedOrganizations(newValues)
      } else {
        const allIsLast = newValue[newValue.length - 1].OrganizationCode === 'All'
        if (allIsLast) {
          setSelectedOrganizations([ALL_ORGANIZATION_OPTION])
        } else {
          setSelectedOrganizations(newValue)
        }
      }
    }
  }

  const handleOpenCalendar = (e) => {
    setAnchorEl(e.currentTarget)
    setDisplayCalendar(true)
  }

  const handleCloseCalendar = () => {
    setDisplayCalendar(false)
    setAnchorEl(null)
  }

  const handleDateRangeChange = (event) => {
    const inputValue = event.target.value
    let [startDate, endDate] = inputValue.split('-').map((elm) => elm.trim())
    
    let dateStart = new Date(startDate)
    dateStart = isValid(dateStart) ? dateStart : undefined

    let dateEnd = new Date(endDate)
    dateEnd = isValid(dateEnd) ? dateEnd: undefined
    if (isValidDate(startDate) && isValidDate(endDate)) {
      setDateRangeError(false)
      if (isBefore(dateStart, dateEnd) || isSameDay(dateStart, dateEnd)) {
        fetchKpis()
      } else {
        setDateRangeError(true)
      }
    } else {
      setDateRangeError(true)
    }

    setKpiDates({ 
      startDate: dateStart,
      endDate: dateEnd,
      dateRange: inputValue
    })
  }

  const handleSelectDateRange = ({ selection }) => {
    let { startDate, endDate } = selection

    let dateStart = new Date(startDate)
    dateStart = isValid(dateStart) ? dateStart : undefined
    let dateEnd = new Date(endDate)
    dateEnd = isValid(dateEnd) ? dateEnd: undefined

    let inputValue = ''
    if (dateStart) inputValue += format(dateStart, 'MM/dd/yyyy')
    if (dateEnd) inputValue += ' - ' + format(dateEnd, 'MM/dd/yyyy')

    if (isValidDate(format(dateStart, 'MM/dd/yyyy')) && isValidDate(format(dateEnd, 'MM/dd/yyyy'))) {
      setDateRangeError(false)
      if (isBefore(dateStart, dateEnd) || isSameDay(dateStart, dateEnd)) {
        fetchKpis()
      } else {
        setDateRangeError(true)
      }
    } else {
      setDateRangeError(true)
    }

    setKpiDates({ 
      startDate: dateStart,
      endDate: dateEnd,
      dateRange: inputValue
    })
  }

  return (
    <div>
      <Box 
        component='div' 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column' },
          justifyContent: 'space-between'
        }}
      >
        <Box
          component='div'
          sx={{
            display: 'flex'
          }}
        >
          <Typography
            variant='h4'
            sx={{
              color: '#27417A',
              fontWeight: 'bold'
            }}
          >
            KPI Dashboard&nbsp;
          </Typography>
          <Typography
            variant='h4'
            sx={{
              color: '#3A6DAA',
              fontWeight: 'bold'
            }}
          >
            COVID-19
          </Typography> 
        </Box>
        <Autocomplete
          sx={{ my: 2 }}
          multiple
          disableCloseOnSelect
          id='tags-organizations'
          options={isSuccess ? addAllOption(organizations, ALL_ORGANIZATION_OPTION) : []}
          getOptionLabel={(option) => option.Name}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.Name}
            </li>
          )}
          value={selectedOrganizations}
          onChange={handleChangeOrganizations}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Organizations'
            />
          )}
        />
      </Box>
      <Box sx={{ width: '100%' }}>
        <Box>
          <Tabs 
            value={kpiTimeSpan} 
            onChange={handleChange}
            aria-label='nav tabs kpi'
            TabIndicatorProps={{
              style: {
                display: 'none'
              }
            }}
            sx={{
              ml: -2
            }}
          >
            {
              kpiTabs.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  sx={{
                    textDecoration: kpiTimeSpan === index ? 'none' : 'underline',
                    color: '#27417A !important',
                    fontWeight: kpiTimeSpan === index ? 'bold' : '',
                  }}
                  value={index}
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  {...a11yProps(index)}
                />
              ))
            }
          </Tabs> 
        </Box>
        <TabPanel
          value={kpiTimeSpan}
          index={0}
        >
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'flex', xs: 'none' }
            }}
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  lg={12}
                  md={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'none', xs: 'center' }
            }}
            align='center'
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  sm={12} 
                  xs={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  sm={12} 
                  xs={12}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
        </TabPanel>
        <TabPanel
          value={kpiTimeSpan}
          index={1}
        >
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'flex', xs: 'none' }
            }}
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  lg={12}
                  md={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'none', xs: 'center' }
            }}
            align='center'
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  sm={12} 
                  xs={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  sm={12} 
                  xs={12}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
        </TabPanel>
        <TabPanel
          value={kpiTimeSpan}
          index={2}
        >
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'flex', xs: 'none' }
            }}
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  lg={12}
                  md={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'none', xs: 'center' }
            }}
            align='center'
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  sm={12} 
                  xs={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  sm={12} 
                  xs={12}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
        </TabPanel>
        <TabPanel
          value={kpiTimeSpan}
          index={3}
        >
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'flex', xs: 'none' }
            }}
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  lg={12}
                  md={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'none', xs: 'center' }
            }}
            align='center'
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  sm={12} 
                  xs={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  sm={12} 
                  xs={12}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
        </TabPanel>
        <TabPanel
          value={kpiTimeSpan}
          index={4}
        >
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'flex', xs: 'none' }
            }}
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  lg={12}
                  md={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  lg={3} 
                  md={4}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'none', xs: 'center' }
            }}
            align='center'
          >
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  sm={12} 
                  xs={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                <Grid 
                  key={kpi.kpiId}
                  item
                  sm={12} 
                  xs={12}
                >
                  <KPI 
                    homePage
                    kpiId={kpi.kpiId} 
                    name={kpi.name} 
                    current={kpi.current}
                    previous={kpi.previous}
                    timeSpan={kpiTimeSpan}
                    tabs={kpiTabs}
                  />
                </Grid>
              ))
            }
          </Box>
        </TabPanel>
        <TabPanel
          value={kpiTimeSpan}
          index={5}
        >
          <Box
            component={Grid}
            container 
            spacing={2}
            sx={{
              display: { md: 'flex', xs: 'none' }
            }}
          >
            <Grid
              item
              xs={12}
            >
              <TextField
                label='Date Range'
                sx={{ 
                  width: '55%'
                }}
                value={kpiDateRange}
                size='small'
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton onClick={handleOpenCalendar}>
                        <DateRangeIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onChange={handleDateRangeChange}
              />
              <Popover
                open={displayCalendar}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                onClose={handleCloseCalendar}
              >
                <Box>
                  <DateRange
                    ranges={[{
                      startDate: kpiStartDate,
                      endDate: kpiEndDate,
                      key: 'selection'
                    }]}
                    rangeColors={['#3A6DAA']}
                    onChange={handleSelectDateRange}
                    maxDate={new Date()}
                    showMonthAndYearPickers
                    moveRangeOnFirstSelection={false}
                    showDateDisplay={false}
                  />
                </Box>
              </Popover>
            </Grid>
            {
              (loadingKpis || isRefetching) && (
                <Grid
                  item
                  lg={12}
                  md={12}
                >
                  <CircularProgress />
              Loading KPIs
                </Grid>
              )
            }
            {
              dateRangeError ? (
                <Alert severity='error' sx={{ mt: 2 }}>
                  Please enter valid date range using this format MM/DD/YYYY - MM/DD/YYYY
                </Alert>
              ) : (
                <>
                  {
                    (loadedKpis && !isRefetching) && kpis.map((kpi) => (
                      <Grid 
                        key={kpi.kpiId}
                        item
                        lg={3} 
                        md={4}
                      >
                        <KPI 
                          homePage
                          kpiId={kpi.kpiId} 
                          name={kpi.name} 
                          current={kpi.current}
                          previous={kpi.previous}
                          timeSpan={kpiTimeSpan}
                          tabs={kpiTabs}
                        />
                      </Grid>
                    ))
                  }
                </>
              )
            }
          </Box>
        </TabPanel>
      </Box>
    </div>
  )
}

export default Home